import { environment } from '@env/environment';
import { Brand } from '../model/brand.model';
import { resolveBrandForHost } from '../model/brand.registry';

/** localStorage key that persists a dev brand override across navigations. */
export const BRAND_OVERRIDE_STORAGE_KEY = 'brand-override';

/**
 * Reads a dev-only brand override so the parallel run can be exercised locally
 * without DNS. Honoured only outside production builds. A `?brand=<key>` query
 * param takes effect immediately and is persisted to localStorage so it survives
 * later navigations; with no query param, any previously stored value is used.
 * Any failure to read the browser APIs resolves to "no override".
 */
export function readDevBrandOverride(): string | null {
  if (environment.production) {
    return null;
  }

  try {
    const fromQuery = new URLSearchParams(window.location.search).get('brand');
    if (fromQuery) {
      window.localStorage.setItem(BRAND_OVERRIDE_STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    return window.localStorage.getItem(BRAND_OVERRIDE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Resolves the brand for the current window host, applying any dev override.
 * The single runtime entry point shared by BrandService and the pre-bootstrap
 * chrome, so both always see the same brand.
 */
export function resolveActiveBrand(): Brand {
  return resolveBrandForHost(window.location.host, readDevBrandOverride());
}
